<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	// Stores
	import UserStore from '@/Stores/UserStore.js';
	import { triggerUserSearch } from '@/Stores/Store.js';
	import UrlStore from '@/Stores/UrlStore.js';
	// Services
	import { setUrlParameter, paginationBuilder } from '@/Services/Helpers.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Label from '@/Components/Label.svelte';
	import PillTextInput from '@/Components/PillTextInput.svelte';
	// Constants
	import { UserRatingColours, UserSubRatingColours } from '@/Constants/User.js';
	import Helmet from '@/Components/Helmet.svelte';

	$: links = $UserStore.count() && paginationBuilder(UserStore.getPagination());

	let initialParams = new URLSearchParams(document.location.search.substring(1));

	$: pageNo =
		$UrlStore &&
		$UrlStore.params &&
		($UrlStore.params.get('page') || (initialParams.has('page') ? initialParams.get('page') : 1));
	$: term =
		$UrlStore &&
		$UrlStore.params &&
		($UrlStore.params.get('term') || (initialParams.has('term') ? initialParams.get('term') : null));
	$: field =
		$UrlStore &&
		$UrlStore.params &&
		($UrlStore.params.get('field') || (initialParams.has('field') ? initialParams.get('field') : null));

	$: (term || !term) && (field || !field) && pageNo && getUsers(pageNo, term, field);

	let loading = true;

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			getUsers(pageNo, term, field);
			setUrlParameter('page', pageNo);
		}
	}

	async function getUsers(page, searchTerm, searchField) {
		loading = true;
		await UserStore.getUsers({ size: 50, number: page }, searchTerm, searchField).then(() => {
			if (searchTerm && $UserStore.count() === 1) {
				click(String($UserStore.pluck('id').first()));
			} else {
				triggerUserSearch.set(false);
			}
		});
		loading = false;
	}

	function click(id) {
		navigate(`/users/${id}`);
	}

	function clearSearch() {
		navigate('/users');
		triggerUserSearch.set(true);
	}
</script>

<Helmet title="Users" />

{#if loading}
	<Loading />
{:else}
	<!-- Page Header -->
	<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
		<div class="flex items-center w-full">
			<h1 class="text-xl font-bold">Users</h1>
		</div>
	</div>
	<!-- /Page Header -->
	<div class="p-2 mx-4 bg-gray-100 rounded">
		<Pagination {links} on:click={changePage} className="mt-2" />
		{#if term}
			<div class="flex items-center font-semibold">
				Search Results for
				<PillTextInput
					action="remove"
					on:remove={clearSearch}
					name={term}
					bgColour="bg-gray-300"
					textColour="text-dark"
					className="m-2 inline-flex"
					label={`${field}:  ${term}`}
					value={false}
				/>
			</div>
		{/if}
		<div class="overflow-x-auto rounded">
			<table class="w-full whitespace-nowrap">
				<thead>
					<tr class="text-xs text-left text-gray-500 uppercase">
						<th class="px-6 pt-5 pb-4 font-medium">PIN</th>
						<th class="px-6 pt-5 pb-4 font-medium">Username</th>
						<th class="px-6 pt-5 pb-4 font-medium">First Name</th>
						<th class="px-6 pt-5 pb-4 font-medium">Last Name</th>
						<th class="px-6 pt-5 pb-4 font-medium">Status</th>
						<th class="px-6 pt-5 pb-4 font-medium" />
					</tr>
				</thead>
				<tbody class="bg-white">
					{#each $UserStore.all() as user (user.id)}
						<tr on:click={() => click(user.id)} class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
							<td class="p-4 px-6 font-semibold border-t">{user.pin}</td>
							<td class="p-4 px-6 font-semibold border-t">{user.name}</td>
							<td class="p-4 px-6 font-semibold border-t">
								{user.first_name}
							</td>
							<td class="p-4 px-6 font-semibold border-t">
								{user.last_name}
							</td>
							<td class="p-4 px-6 font-semibold border-t">
								<div class="flex items-center space-x-2">
									<Label label={user.status.name} />
									<div
										class="focus:outline-none rounded px-2 py-1 text-xs
                      text-center {UserRatingColours[user.rating.id].bg}
                      {UserRatingColours[user.rating.id].text}"
									>
										{user.rating.name}
									</div>
									{#if user.sub_rating}
										<div
											class="focus:outline-none rounded px-2 py-1 text-xs
						  text-center {UserSubRatingColours?.[user.sub_rating?.id]?.bg || 'bg-gray-100'}
						  {UserRatingColours[user.sub_rating.id].text}"
										>
											{user.sub_rating.name}
										</div>
									{/if}
									{#if user.tier.name}
										<Label label={user.tier.name ? '$' + user.tier.name : null} />
									{/if}
									{#if user.message}
										<div title={user.message}>
											<Icon name="info" classes="w-4 h-4 text-red-500" />
										</div>
									{/if}
								</div>
							</td>
							<td class="w-px px-6 border-t">
								<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
							</td>
						</tr>
					{:else}
						<tr>
							<td class="px-6 py-4 text-center border-t" colspan="6"> No users found. </td>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
		<Pagination {links} on:click={changePage} className="my-3" />
	</div>
{/if}
